import { useState, type FormEvent } from 'react';
import {
  CASH_ROUNDING_OPTIONS,
  MAX_ADJUST_BPS,
  MIN_ADJUST_BPS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_KEYS,
  formatAdjust,
  type PaymentMethod,
  type PosSettings,
} from '@pos/core';
import { patchRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Field, Input, PageHeader, Select, Toggle, errorMessage } from '../../ui/index.tsx';
import { usePosSettings } from '../pos/state.ts';

/** "−10" / "15" / "" → puntos básicos (null si es inválido). */
export function parseAdjustPct(text: string): number | null {
  const s = text.trim().replace('−', '-').replace(',', '.');
  if (s === '') return 0;
  if (!/^[-+]?\d+(\.\d{1,2})?$/.test(s)) return null;
  const bps = Math.round(Number(s) * 100);
  return bps < MIN_ADJUST_BPS || bps > MAX_ADJUST_BPS ? null : bps;
}

const pctText = (bps: number) => (bps === 0 ? '' : String(bps / 100).replace('.', ','));

export function PosSettingsPage() {
  const { business, writer } = useSession();
  const current = usePosSettings();
  const [form, setForm] = useState<PosSettings>(current);
  const [adjust, setAdjust] = useState<Record<PaymentMethod, string>>(
    () => Object.fromEntries(PAYMENT_METHOD_KEYS.map((m) => [m, pctText(current.payments[m].adjust_bps)])) as Record<PaymentMethod, string>,
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const invalid = PAYMENT_METHOD_KEYS.filter((m) => parseAdjustPct(adjust[m]) === null);
  const noneEnabled = !PAYMENT_METHOD_KEYS.some((m) => form.payments[m].enabled);

  const setMethod = (m: PaymentMethod, enabled: boolean) => setForm((f) => ({ ...f, payments: { ...f.payments, [m]: { ...f.payments[m], enabled } } }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (invalid.length || noneEnabled) return;
    const pos: PosSettings = {
      ...form,
      payments: Object.fromEntries(PAYMENT_METHOD_KEYS.map((m) => [m, { enabled: form.payments[m].enabled, adjust_bps: parseAdjustPct(adjust[m])! }])) as PosSettings['payments'],
    };
    setSaving(true);
    setMessage(null);
    try {
      await patchRow(db, 'businesses', business.id, { settings: { ...business.settings, pos } }, writer);
      requestSync(0);
      setMessage({ tone: 'success', text: 'Configuración guardada. Se aplica en todos tus dispositivos al sincronizar.' });
    } catch (error) {
      setMessage({ tone: 'error', text: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <PageHeader title="Ventas y cobros" description="Medios de pago, recargos o descuentos por medio, redondeo y ticket." />

      <Card className="space-y-2">
        <h2 className="font-semibold">Medios de pago</h2>
        <p className="text-sm text-slate-500">
          Ajuste por medio: un número negativo es descuento (ej.: −10 en efectivo) y uno positivo es recargo (ej.: 15 en crédito). Se aplica al cobrar,
          sobre el total de la venta. La cuenta corriente aparece solo si se elige un cliente y el usuario tiene permiso.
        </p>
        <ul className="divide-y divide-slate-100">
          {PAYMENT_METHOD_KEYS.map((m) => {
            const bps = parseAdjustPct(adjust[m]);
            return (
              <li key={m} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-40 flex-1">
                  <Toggle checked={form.payments[m].enabled} onChange={(v) => setMethod(m, v)} label={PAYMENT_METHODS[m].label} />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24">
                    <Input
                      inputMode="decimal"
                      compact
                      placeholder="0"
                      aria-label={`Ajuste para ${PAYMENT_METHODS[m].label} en %`}
                      value={adjust[m]}
                      invalid={bps === null}
                      disabled={!form.payments[m].enabled}
                      onChange={(e) => setAdjust((a) => ({ ...a, [m]: e.target.value }))}
                    />
                  </div>
                  <span className="w-28 text-sm text-slate-500">
                    {bps === null ? 'inválido' : bps === 0 ? '% sin ajuste' : `% (${bps < 0 ? 'descuento' : 'recargo'} ${formatAdjust(bps)})`}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
        {invalid.length > 0 && <Alert tone="error">Los ajustes van de −50 % a +100 %, con hasta 2 decimales.</Alert>}
        {noneEnabled && <Alert tone="error">Tiene que haber al menos un medio de pago habilitado.</Alert>}
      </Card>

      <Card className="space-y-2">
        <h2 className="font-semibold">Caja</h2>
        <Toggle
          checked={form.require_open_cash}
          onChange={(v) => setForm((f) => ({ ...f, require_open_cash: v }))}
          label="Pedir caja abierta para vender"
          description="Así cada venta en efectivo queda en el arqueo de la caja del dispositivo."
        />
        <Toggle
          checked={form.blind_close}
          onChange={(v) => setForm((f) => ({ ...f, blind_close: v }))}
          label="Arqueo ciego para empleados"
          description="El empleado cuenta el efectivo sin ver cuánto debería haber; la diferencia la ve después de cerrar."
        />
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">Cobro y stock</h2>
        <Field label="Redondeo del efectivo" hint="Redondea el total a cobrar en efectivo al múltiplo más cercano (ej.: $1.234 → $1.230).">
          {(id) => (
            <Select id={id} value={form.cash_rounding} onChange={(e) => setForm((f) => ({ ...f, cash_rounding: Number(e.target.value) }))}>
              {CASH_ROUNDING_OPTIONS.map((o) => (
                <option key={o.cents} value={o.cents}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Vender sin stock" hint="Solo aplica a productos con control de stock.">
          {(id) => (
            <Select id={id} value={form.negative_stock} onChange={(e) => setForm((f) => ({ ...f, negative_stock: e.target.value as PosSettings['negative_stock'] }))}>
              <option value="warn">Avisar y dejar vender</option>
              <option value="block">No dejar vender</option>
            </Select>
          )}
        </Field>
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">Ticket</h2>
        <Field label="Ancho del papel">
          {(id) => (
            <Select id={id} value={form.ticket_width} onChange={(e) => setForm((f) => ({ ...f, ticket_width: Number(e.target.value) as 58 | 80 }))}>
              <option value={80}>80 mm</option>
              <option value={58}>58 mm</option>
            </Select>
          )}
        </Field>
        <Field label="Mensaje al pie" hint="Ej.: horarios, redes, «Cambios dentro de los 30 días con ticket».">
          {(id) => <Input id={id} maxLength={200} value={form.ticket_footer} onChange={(e) => setForm((f) => ({ ...f, ticket_footer: e.target.value }))} />}
        </Field>
        <Toggle
          checked={form.ticket_logo}
          onChange={(v) => setForm((f) => ({ ...f, ticket_logo: v }))}
          label="Imprimir el logo"
          description={business.logo_path ? 'Arriba del ticket, en blanco y negro.' : 'Primero cargá el logo en Configuración → Negocio.'}
        />
        <p className="text-sm text-slate-500">El ticket usa el logo, el nombre, la dirección y el teléfono de «Negocio». No es una factura (eso llega con ARCA).</p>
      </Card>

      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <div className="flex justify-end">
        <Button type="submit" loading={saving} disabled={invalid.length > 0 || noneEnabled}>
          Guardar
        </Button>
      </div>
    </form>
  );
}
